import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import style from './ZingNotifications.module.css'
import DialogModal from '../DialogModal/DialogModal'
import { closeReadComPort } from '../../WebSerialConnection/SerialUSBConnect'
import { isZingNotificationDevice } from '../../../utils/zingNotifications'
import { getConnectedDeviceImage } from '../../../hooks/useBrandConfig'
import renderImage from '../../../source/importImg'

const SUCCESS_HOLD_MS = 1200
const EXIT_MS = 320

function ZingNotificationHost() {
    const dispatch = useDispatch()
    const history = useHistory()
    const { isFalling, motorFaultIds, sdCardMissing } = useSelector(
        (state) => state.zingNotification
    )
    const webSerial = useSelector((state) => state.webSerial)
    const worker = useSelector((state) => state.worker)
    const device = useSelector((state) => state.device)

    // null -> 'success' -> 'exit' -> null; only entered when a fall that this
    // session was showing is reported recovered while still connected.
    const [phase, setPhase] = useState(null)
    const wasFalling = useRef(false)
    const eligible =
        webSerial.isConnected &&
        isZingNotificationDevice(device.connectedDevice, device.deviceVersion)
    const faultActive = sdCardMissing || motorFaultIds.length > 0

    useEffect(() => {
        if (!webSerial.isConnected) dispatch({ type: 'ZING_NOTIFICATION_RESET' })
    }, [webSerial.isConnected, dispatch])

    useEffect(() => {
        if (!eligible) {
            wasFalling.current = false
            setPhase(null)
        } else if (isFalling) {
            wasFalling.current = true
            setPhase(null)
        } else if (wasFalling.current) {
            wasFalling.current = false
            if (!faultActive) setPhase('success')
        }
    }, [eligible, isFalling, faultActive])

    useEffect(() => {
        if (!phase) return undefined
        const timer = setTimeout(
            () => setPhase(phase === 'success' ? 'exit' : null),
            phase === 'success' ? SUCCESS_HOLD_MS : EXIT_MS
        )
        return () => clearTimeout(timer)
    }, [phase])

    if (!eligible) return null

    const disconnect = async () => {
        await closeReadComPort(webSerial.port, worker)
        dispatch({ type: 'WEB_SERIAL_PORT_DISCONNECT' })
        dispatch({ type: 'ZING_NOTIFICATION_RESET' })
        history.push('/kitSelection')
    }

    // SD card takes priority; motor fault next. Fall recovery never hides either.
    if (sdCardMissing) {
        return (
            <DialogModal
                show
                preventBackdropClose
                showCloseBtn={false}
                text={
                    <div className={style.dialogBody}>
                        <h3 className={style.dialogTitle}>No SD Card Detected</h3>
                        <p>
                            The SD card is not detected in your Zing device. Please insert
                            the SD card properly and try again.
                        </p>
                    </div>
                }
                optionsToSelect={[{ text: 'OK', do: disconnect }]}
            />
        )
    }

    if (motorFaultIds.length) {
        // No button: the firmware sends the motor-link bytes again once the
        // servo is reconnected, and that clears this automatically.
        return (
            <div
                className={style.fallOverlay}
                role="alertdialog"
                aria-live="assertive"
                aria-label="Servo motor check failed"
                data-testid="zing-motor-overlay"
                onKeyDown={(e) => e.preventDefault()}
                tabIndex={-1}
                ref={(el) => el && el.focus()}
            >
                <div className={style.fallCard}>
                    <span className={style.fallSpinner} />
                    <h3 className={style.fallTitle}>Servo Motor Check Failed</h3>
                    <p className={style.fallText}>
                        {motorFaultIds.length === 1
                            ? 'A servo motor is not responding.'
                            : 'Some servo motors are not responding.'}
                    </p>
                    <p className={style.servoIds}>
                        {motorFaultIds.map((id) => (
                            <span key={id} className={style.servoChip}>
                                Servo {id}
                            </span>
                        ))}
                    </p>
                    <p className={style.fallText}>
                        Please check the motor connection and wiring. This message
                        goes away on its own once the servo is connected again.
                    </p>
                </div>
            </div>
        )
    }

    // True on the very render the fall clears, before the effect has set the
    // phase, so the overlay never blinks out between the two states.
    const justRecovered = !isFalling && wasFalling.current && !faultActive

    if (isFalling || phase || justRecovered) {
        // One overlay for both states, so recovery morphs in place instead of
        // swapping cards. Blocks all input until the fade-out starts.
        const recovered = !isFalling
        const zingImage =
            getConnectedDeviceImage(
                device.connectedDevice,
                device.deviceVersion
            ) ||
            renderImage(
                String(device.deviceVersion || '').startsWith('3')
                    ? 'humanoid3'
                    : 'humanoid'
            )

        return (
            <div
                className={`${style.fallOverlay} ${
                    recovered ? style.recovered : ''
                } ${phase === 'exit' ? style.fallOverlayExit : ''}`}
                role={recovered ? 'status' : 'alertdialog'}
                aria-live={recovered ? 'polite' : 'assertive'}
                aria-label={
                    recovered
                        ? 'Zing has recovered'
                        : 'Zing is recovering from a fall'
                }
                data-testid={
                    recovered ? 'zing-recovered-overlay' : 'zing-fall-overlay'
                }
                onKeyDown={(e) => e.preventDefault()}
                tabIndex={-1}
                ref={(el) => el && el.focus()}
            >
                <div className={style.fallCard}>
                    <div className={style.zingOrb} aria-hidden="true">
                        <span className={style.orbHalo} />
                        <svg className={style.orbRing} viewBox="0 0 100 100">
                            <defs>
                                <linearGradient
                                    id="zingFallArc"
                                    x1="0"
                                    y1="0"
                                    x2="1"
                                    y2="1"
                                >
                                    <stop offset="0%" stopColor="#ffb199" />
                                    <stop offset="100%" stopColor="#f97d62" />
                                </linearGradient>
                            </defs>
                            <circle
                                className={style.orbTrack}
                                cx="50"
                                cy="50"
                                r="46"
                            />
                            <circle
                                className={style.orbArc}
                                cx="50"
                                cy="50"
                                r="46"
                            />
                            <circle
                                className={style.orbDone}
                                cx="50"
                                cy="50"
                                r="46"
                            />
                        </svg>
                        <img
                            className={style.orbZing}
                            src={zingImage}
                            alt=""
                            draggable="false"
                            onError={(e) => {
                                e.currentTarget.style.visibility = 'hidden'
                            }}
                        />
                        <span className={style.orbBadge}>
                            <svg viewBox="0 0 24 24">
                                <path d="M6.5 12.5l3.5 3.5 7.5-8" />
                            </svg>
                        </span>
                    </div>

                    {/* Both texts share one grid cell, so the card keeps the
                        height of the taller one and never jumps on recovery. */}
                    <div className={style.fallCopyStack}>
                        <div
                            className={
                                recovered ? style.fallCopyIdle : style.fallCopy
                            }
                            aria-hidden={recovered}
                        >
                            <h3 className={style.fallTitle}>Hang tight!</h3>
                            <p className={style.fallText}>
                                Zing took a little tumble and is getting back up
                                on its own.
                            </p>
                            <p className={style.fallHint}>
                                You can continue once it's on its feet
                                <span className={style.fallDots} />
                            </p>
                        </div>
                        <div
                            className={
                                recovered ? style.fallCopy : style.fallCopyIdle
                            }
                            aria-hidden={!recovered}
                        >
                            <h3 className={style.fallTitle}>
                                Zing is back on its feet!
                            </h3>
                            <p className={style.fallText}>
                                Zing has successfully recovered.
                                <br />
                                You're ready to continue!
                            </p>
                        </div>
                    </div>

                    <span className={style.fallProgress} aria-hidden="true" />
                </div>
            </div>
        )
    }

    return null
}

export default ZingNotificationHost
